import React, { useContext, useMemo } from 'react';

import { AlertWarning } from '@ad-scripts/presentation/components/common/alert';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import { getFriendlyName } from 'src/presentation/components/pages/sidekick/threads/utils/utils';
import {
  HalfRow,
  Row,
} from 'src/presentation/components/pages/sidekick/threads/components/SidePanel/Components/controls/Layout';
import { SelectOption } from 'src/presentation/components/pages/sidekick/threads/components/SidePanel/Components/Rule/helpers/memoriesHelper';
import sidekickIntegrationsResource from 'src/actions/sidekick/sidekickIntegrationsResource';
import SidekickModuleSpec, { ModifierSpec, VCustomerIntegration } from 'src/models/Sidekick/SidekickModuleSpec';
import { SidekickThreadEditorContext } from 'src/presentation/components/pages/sidekick/threads/SidekickThreadEditorContext';
import { ThreadModuleContext } from 'src/presentation/components/pages/sidekick/threads/components/SidePanel/ThreadModuleContext';
import { useResourceValues } from 'src/presentation/hooks/useResource';

type IntegrationsProps = {
  spec: SidekickModuleSpec | ModifierSpec;
};

const Integrations = ({ spec }: IntegrationsProps) => {
  const { values: integrations } = useResourceValues(sidekickIntegrationsResource);

  if (spec.integrations == null || !spec.integrations.length) {
    return <></>;
  }

  if (!Object.keys(integrations || {})?.length) {
    return <IntegrationsSetupLink />;
  }

  return <IntegrationsControls spec={spec} integrations={integrations} />;
};

const IntegrationsSetupLink = () => {
  const { thankfulUrl } = useContext(SidekickThreadEditorContext);

  return (
    <Row>
      <AlertWarning>
        <b>Warning!</b>{' '}
        <ExternalLink label="Add a compatible Threads integration" to={`${thankfulUrl}/settings/integrations`} />
        &nbsp;to configure this.
      </AlertWarning>
    </Row>
  );
};

type IntegrationsControlsProps = {
  spec: SidekickModuleSpec | ModifierSpec;
  integrations: Record<string, VCustomerIntegration[]>;
};

const IntegrationsControls = ({ spec, integrations }: IntegrationsControlsProps) => {
  return (
    <>
      {spec.integrations!.map(key => (
        <IntegrationControl key={key} integrationName={key} integrations={integrations} />
      ))}
    </>
  );
};

type IntegrationControlProps = {
  integrationName: string;
  integrations: Record<string, VCustomerIntegration[]>;
};

const IntegrationControl = ({ integrationName, integrations }: IntegrationControlProps) => {
  const { readOnly } = useContext(ThreadModuleContext);

  const options = useMemo(() => {
    if (!integrations[integrationName]) {
      return [];
    }

    return integrations[integrationName].map(
      (ig: VCustomerIntegration): SelectOption => ({
        label: `${ig.name} - ${ig.customerIntegrationID}`,
        value: ig.customerIntegrationID,
      })
    );
  }, [integrationName, integrations]);

  const name = getFriendlyName(integrationName);

  if (!options.length) {
    return <NoOptions friendlyName={name} />;
  }

  return (
    <HalfRow>
      <Dropdown
        attr={`integrations.${integrationName}`}
        disabled={readOnly}
        fitOpenerWidth
        label={name}
        options={options}
        placeholder="Select an integration"
      />
    </HalfRow>
  );
};

const NoOptions = ({ friendlyName }: { friendlyName: string }) => {
  const { thankfulUrl } = useContext(SidekickThreadEditorContext);

  return (
    <Row>
      <AlertWarning>
        <b>Warning!</b> No {friendlyName} integration configured. Go to&nbsp;
        <ExternalLink label="Integrations" to={`${thankfulUrl}/settings/integrations`} />
        &nbsp;to get started.
      </AlertWarning>
    </Row>
  );
};

export default Integrations;
